import { FormEvent, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { areBothDatesPresent, formatDate, isOneDatePresent } from '../../../utils/helpers';
import { useGetAllBatches } from '../../../apis/batches/get-all-batches';
import { useCloseBatch } from "../../../apis/batches/close-batch";
import { toast } from "react-toastify";

const useBatches = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const form = useRef<HTMLFormElement>(null);

    const [status, setStatus] = useState(searchParams.get("status") || "");
    const [fromDate, setFromDate] = useState<string | null>(searchParams.get("fromDate") || "");
    const [toDate, setToDate] = useState<string | null>(searchParams.get("toDate") || "");
    const [selectedBatchId, setSelectedBatchId] = useState("");

    const { data, isFetching: loading, refetch } = useGetAllBatches({
        limit: 10,
        fromdate: searchParams.get("fromDate") || "",
        todate: searchParams.get("toDate") || "",
        status: searchParams.get("status") || "",
        page: parseInt(searchParams.get("page") || "1"),
    });

    const { mutateAsync: closeBatch, isPending: isClosingBatch } = useCloseBatch();

    const handleServiceCancel = async () => {
        if (!selectedBatchId) return;
        try {
            await closeBatch(selectedBatchId);
            toast.success("Batch Closed Successfully");
            refetch();
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Couldn't close batch at this moment. Please try again later.");
        } finally {
            setSelectedBatchId("");
        }
    };

    const filterBatches = (e: FormEvent) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        const newParams = new URLSearchParams();

        for (const [key, value] of formData.entries()) {
            if (value) newParams.append(key, value.toString());
        }

        if (fromDate) newParams.set("fromDate", fromDate);
        if (toDate) newParams.set("toDate", toDate);
        newParams.set("page", "1");
        navigate(`?${newParams.toString()}`);
    };

    const handlePageClick = (e: { selected: number }) => {
        searchParams.set("page", (e.selected + 1).toString());
        navigate(`?${searchParams.toString()}`);
    };

    const resetFilters = () => {
        setStatus("");
        setFromDate("");
        setToDate("");
        form.current?.reset();
        navigate("");
    };

    const handleFromDate = (e: Date | null | undefined) =>
        setFromDate(formatDate(e?.toString() || ""));

    const handleToDate = (e: Date | null | undefined) =>
        setToDate(formatDate(e?.toString() || ""));

    const isAnyFilterApplied = () => status !== "";

    const isApplyButtonDisabled = () => {
        if (isAnyFilterApplied()) return isOneDatePresent(fromDate, toDate);
        return !areBothDatesPresent(fromDate, toDate);
    };

    const isClearButtonDisabled = () =>
        status === "" && fromDate === "" && toDate === "";

    return {
        form,
        batches: data?.docs ?? [],
        totalPage: data?.totalPages ?? 1,
        loading,
        status,
        setStatus,
        fromDate,
        toDate,
        handleFromDate,
        handleToDate,
        filterBatches,
        handlePageClick,
        resetFilters,
        isApplyButtonDisabled,
        isClearButtonDisabled,
        searchParams,
        isClosingBatch,
        selectedBatchId,
        setSelectedBatchId,
        handleServiceCancel,
        refetch,
    };
};

export default useBatches;